// Copyright (c) Docugami, Inc. All rights reserved.

import { DocSourceId, ReviewDocsId } from "components/myContentExplorer/ExplorerTypes";
import { NavigationType, useNavigationService } from "hooks/navigationService";
import { useServiceNotificationMarkAsRead } from "hooks/ServiceNotificationMarkAsRead";
import { BuildState, ClusterInfo } from "networking/api";
import { BuildType } from "networking/BuildType";
import { BuildStatusContext, InteractionStates, NotificationModel } from "networking/NotificationsApi"
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { mapVisibleUserClusters } from "store/state_helpers";
import { NotificationItemClickedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { NotificationItem, NotificationItemProps } from "./NotificationItem";

interface BuildStatusNotificationProps {
    model: NotificationModel;
    context: BuildStatusContext;
}

export const BuildStatusNotification = (props: BuildStatusNotificationProps): JSX.Element | null => {
    const [message, setMessage] = useState('');
    const userDocSets = useSelector((state: AppState) => mapVisibleUserClusters(state.clusters));
    const navService = useNavigationService();

    const markAsRead = useServiceNotificationMarkAsRead();

    function primaryAction() {
        Telemetry.trackEvent<NotificationItemClickedSchema>({
            id: props.model.id,
            category: props.model.category,
            type: props.model.notificationType,
            eventName: TelemetryEvent.NotificationItemClicked
        });

        markAsRead([props.model.id], true);

        const targetDocSetId: string = getTargetDocSetIdForBuildStatus(props.context, userDocSets);
        navService.navToDocSetPage(NavigationType.PushNewPage, targetDocSetId);
    }

    useEffect(() => {
        setMessage(getBuildStatusMessage(props.context.type, props.context.state));
    }, [props.context])

    const itemProps: NotificationItemProps = {
        primaryAction: { action: primaryAction, label: 'View' },
        message: message,
        createdAt: props.model.createdAt,
        isRead: !!(props.model.interactions & InteractionStates.Read),
        progressVisual: props.context.state === BuildState.InProgress ? {} : undefined
    }

    return !!message ? <NotificationItem {...itemProps} /> : null;
}

function getTargetDocSetIdForBuildStatus(context: BuildStatusContext, docSets: ClusterInfo[]): string {
    switch (context.type) {
        case BuildType.Ingestion:
            return DocSourceId.Docugami;

        case BuildType.Clustering:
            return context.state === BuildState.Succeeded ? docSets[0]?.clusterId! ?? DocSourceId.Docugami : ReviewDocsId.Unassigned;
    }

    return '';
}

function getBuildStatusMessage(buildType: BuildType, buildState: BuildState) {
    switch (buildType) {
        case BuildType.Ingestion:
            return getIngestionMessage(buildState)

        case BuildType.Clustering:
            return getClusteringMessage(buildState);
    }

    return '';
}

function getIngestionMessage(state: BuildState) {
    switch (state) {
        case BuildState.Succeeded:
            return 'Your documents have been successfully processed!';

        case BuildState.InProgress:
            return 'Your documents are processing...';

        default:
            // Failed
            return 'We were unable to process some of your documents.';
    }
}

function getClusteringMessage(state: BuildState) {
    switch (state) {
        case BuildState.Succeeded:
            return 'We have finished automatically assigning your documents to doc sets!';

        case BuildState.InProgress:
            return 'Your documents are being automatically assigned to doc sets...';

        default:
            // Failed
            return 'There was an issue assigning your documents to doc sets. You can always assign them manually in the Doc Explorer.';
    }
}
